import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Field, Input, Modal, RadioGroup } from '@bb/ui';
import { ApiError, type AdminApi } from '../../api/client.ts';

/** «Create Canvas»: a name and a starting graph (empty or a template); steps are built in the editor (BB-114). */
export function CreateCanvasDialog({ environmentId, open, onOpenChange }: { environmentId: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('blank');
  const templates = useQuery({ queryKey: ['canvas-templates', environmentId], queryFn: () => api.canvasTemplates(environmentId), enabled: open });
  const create = useMutation({
    mutationFn: () => api.createCanvas(environmentId, { name: name.trim(), templateId }),
    onSuccess: async (created) => {
      onOpenChange(false);
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['canvases', environmentId] });
      await navigate({ to: '/env/$environmentId/canvas/$canvasId', params: { environmentId, canvasId: created.id } });
    },
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Create Canvas"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!name.trim()} loading={create.isPending} onClick={() => create.mutate()}>
            Create draft
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </Field>
      {templates.data && (
        <RadioGroup
          label="Start from"
          value={templateId}
          onValueChange={setTemplateId}
          options={templates.data.items.map((t) => ({ value: t.id, label: `${t.name} — ${t.description}` }))}
        />
      )}
      {create.isError && <p role="alert">{create.error instanceof ApiError ? create.error.message : 'Something went wrong.'}</p>}
    </Modal>
  );
}
